import { Box, Stack, useMantineTheme } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef } from 'react';

import { Button } from '~/domains/design-system/Button';
import type { CryptoDepositNetwork } from '~/services/wallet/interfaces/crypto-direct.interface';
import dt from '~/testing';

import { CryptoQrCode } from './CryptoQrCode';

const visuallyHidden = {
  position: 'absolute' as const,
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap' as const,
};

type CryptoAddressPanelProps = {
  network: CryptoDepositNetwork;
  /** Optional asset icon centred over the QR code (see CryptoQrCode). */
  assetIconUrl?: string | null;
};

/** Mount with `key={network.address}` so the Copied state resets when the address changes. */
export function CryptoAddressPanel({ network, assetIconUrl }: CryptoAddressPanelProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();
  const clipboard = useClipboard({ timeout: 2000 });

  // This panel renders ONLY once the address is revealed (the sheet's 'success' phase), so its
  // mount ≡ reveal. The Reveal button that had focus unmounts on success, dropping focus to <body>;
  // move it to the Copy control — the actionable element — so keyboard/SR users land somewhere real.
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    copyButtonRef.current?.focus();
  }, []);

  // EDGE-32101: `CryptoDepositNetwork.address` is now optional (the v3 READ GET may omit it — see
  // the interface). This panel only ever renders on the sheet's `success` phase, which is gated on
  // the address being present, so in practice it is always defined here; this narrows the widened
  // type to `string` for the four call sites below and is a defensive no-render if it is ever absent.
  const { address } = network;
  if (!address) return null;

  return (
    <Stack spacing={theme.other.spacing._16} align="center" w="100%">
      <CryptoQrCode
        value={address}
        alt={t('deposits.cryptoDirect.address.qrAlt', { network: network.name })}
        assetIconUrl={assetIconUrl}
      />

      <Box
        component="code"
        data-test-id={dt.payments.deposits.cryptoDirect.address}
        aria-label={t('deposits.cryptoDirect.address.label', { network: network.name })}
        sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 14,
          lineHeight: 1.5,
          wordBreak: 'break-all',
          textAlign: 'center',
          width: '100%',
          padding: theme.other.spacing._12,
          borderRadius: theme.radius.md,
          background: theme.colors.gray[1],
          color: theme.colors.gray2[7],
        }}
      >
        {address}
      </Box>

      <Button
        ref={copyButtonRef}
        variant="secondary-stroke"
        size="medium"
        fullWidth
        onClick={() => clipboard.copy(address)}
        data-test-id={dt.payments.deposits.cryptoDirect.copyButton}
      >
        {clipboard.copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
        <Box component="span" ml={theme.other.spacing._8}>
          {clipboard.copied
            ? t('deposits.cryptoDirect.address.copied')
            : t('deposits.cryptoDirect.address.copy')}
        </Box>
      </Button>
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {clipboard.copied ? t('deposits.cryptoDirect.address.copiedAnnouncement') : ''}
      </Box>
    </Stack>
  );
}
